import { useEffect, useState } from 'react';
import { api } from '@/lib/api-client';
import { useAuthStore } from '@/store/authStore';

export interface MyTenant {
  tenant_cod: string;
  tenant_lib: string;
}

/**
 * Fetches the set of tenants the current CBS operator is entitled to switch into.
 * Re-fetches whenever the auth token changes (e.g. after login or a successful switch).
 *
 * Fail-open: on error, returns an empty list so the header switcher is hidden
 * rather than blocking the entire UI.
 */
export function useMyTenants(): { tenants: MyTenant[]; isLoading: boolean } {
  const token = useAuthStore((s) => s.token);
  const [tenants, setTenants] = useState<MyTenant[]>([]);
  // Initialize from token presence so the first render reflects the pending
  // fetch instead of briefly flashing isLoading=false.
  const [isLoading, setIsLoading] = useState<boolean>(() => Boolean(token));

  useEffect(() => {
    if (!token) {
      setTenants([]);
      return;
    }
    setIsLoading(true);
    let cancelled = false;
    api
      .get<MyTenant[]>('/api/auth/my-tenants')
      .then((t) => {
        if (!cancelled) setTenants(t);
      })
      .catch(() => {
        // Fail-open: hide the switcher rather than block the UI.
        if (!cancelled) setTenants([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return { tenants, isLoading };
}
